import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Initialize auth state: handle an SSO redirect (?qq_token=...) from the
    // QuirkyQode portal first, then fall back to a stored session.
    useEffect(() => {
        const initAuth = async () => {
            const searchParams = new URLSearchParams(window.location.search);
            const qqToken = searchParams.get('qq_token');

            if (qqToken) {
                try {
                    const result = await authService.ssoExchange(qqToken);
                    if (result.success) {
                        setUser(result.user);

                        // Clean SSO query params from the URL
                        const url = new URL(window.location.href);
                        url.searchParams.delete('qq_token');
                        url.searchParams.delete('client_id');
                        window.history.replaceState({}, document.title, url.pathname + (url.search || ''));
                        setLoading(false);
                        return;
                    }
                } catch (err) {
                    console.error('SSO auto-login failed:', err);
                }
            }

            const storedUser = authService.getStoredUser();
            if (storedUser && authService.getToken()) {
                setUser(storedUser);
                // Verify token is still valid
                authService.getCurrentUser()
                    .then(data => {
                        if (data.success) {
                            setUser(data.user);
                            localStorage.setItem('vms_user', JSON.stringify(data.user));
                        }
                    })
                    .catch(() => {
                        authService.logout();
                        setUser(null);
                    })
                    .finally(() => setLoading(false));
            } else {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    const logout = useCallback(async () => {
        authService.logout();
        setUser(null);
    }, []);

    const isAdmin = useCallback(() => {
        return user?.role === 'admin';
    }, [user]);

    const isOperator = useCallback(() => {
        const operatorRoles = ['operator', 'admin', 'packer', 'rto'];
        return operatorRoles.includes(user?.role);
    }, [user]);

    const value = {
        user,
        loading,
        isAuthenticated: !!user,
        isAdmin,
        isOperator,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
